import logging from 'utils/logging';
import { MOMENT_TYPES, NARRATION_KEYS, HOST_STATUS } from '../hostStateSlice';
import {
  GAMESTATE_ROUND,
  GAMESTATE_ROUND_FINAL,
  GAMESTATE_ROUNDSTATE_FINALROUND_QUESTION_INTRO,
  GAMESTATE_ROUNDSTATE_QUESTION_READING,
  FINAL_ROUND_QUESTION_TIME_SECONDS,
} from '../../gamestate/constants';
import { advanceState } from '../../gamestate/GameState';
import { registerCacheReset } from '../momentCacheManager';

const log = logging.getLogger('moments.FinalQuestionRead');
log.setLevel('info');

let triggered = false;

export const resetFinalQuestionReadCache = () => {
  triggered = false;
};
registerCacheReset('round', resetFinalQuestionReadCache);

/**
 * Final round question read moment — multi-playback (mirrors FinalCategoryReveal).
 *
 * Trigger fires once, when entering `final.questionIntro`, and generates BOTH
 * narrations upfront so the clue-read narration is ready the instant the
 * intro state finishes.
 *
 * Sequence:
 *   final.questionIntro → intro narration ("And now your final clue:") plays
 *     → onComplete advances to question.reading
 *   question.reading → clue-read narration plays
 *     → onComplete advances to question.answering
 */
export const FinalQuestionReadTrigger = {
  id: 'FINAL_QUESTION_READ',
  shouldTrigger: ({ currentState, questions }) => {
    if (triggered) return false;

    if (currentState?.currentState !== GAMESTATE_ROUND) return false;
    if (currentState?.round !== GAMESTATE_ROUND_FINAL) return false;
    if (currentState?.roundState !== GAMESTATE_ROUNDSTATE_FINALROUND_QUESTION_INTRO) return false;

    const questionId = currentState?.questionId;
    if (!questionId) return false;

    const question = questions?.find(q => q.id === questionId);
    if (!question) return false;

    triggered = true;
    log.info('Final question read moment triggered (generating intro + clue narrations)');

    return {
      shouldTriggerAction: false,
      shouldGenerateNarration: true,
      multipleNarrations: [
        {
          narrationKey: NARRATION_KEYS.FINAL_QUESTION_INTRO,
          momentType: MOMENT_TYPES.FINAL_QUESTION_INTRO,
          context: {
            category: question.category,
          },
        },
        {
          narrationKey: NARRATION_KEYS.FINAL_QUESTION_READ,
          momentType: MOMENT_TYPES.FINAL_QUESTION_READ,
          context: {
            questionId,
            category: question.category,
            questionText: question.clue,
            timeSeconds: FINAL_ROUND_QUESTION_TIME_SECONDS,
          },
        },
      ],
    };
  },
};

/**
 * Playback 1 — "Wagers are in. And now your final clue:"
 * Plays during `final.questionIntro`. Visually, the modal mounts with the clue
 * fading in (see GameBoardState.js) while this narration plays.
 * onComplete: advance to question.reading.
 */
export const FinalQuestionIntroPlayback = {
  id: 'FINAL_QUESTION_INTRO_PLAYBACK',
  momentType: MOMENT_TYPES.FINAL_QUESTION_INTRO,

  shouldPlay: (gameState, hostState, narration, playedNarrations) => {
    if (!narration || narration.momentType !== MOMENT_TYPES.FINAL_QUESTION_INTRO) return false;
    if (playedNarrations.has(narration.key)) return false;

    const inIntro =
      gameState?.currentState === GAMESTATE_ROUND &&
      gameState?.round === GAMESTATE_ROUND_FINAL &&
      gameState?.roundState === GAMESTATE_ROUNDSTATE_FINALROUND_QUESTION_INTRO;
    if (!inIntro) return false;

    const ready = narration.textStatus === 'READY' && (narration.audioStatus === 'READY' || narration.audioStatus === 'ERROR');
    if (!ready) return false;

    if (hostState?.status === HOST_STATUS.SPEAKING) return false;

    return true;
  },

  onComplete: async (gameId, dispatch, getState) => {
    log.info('Final question intro complete, advancing to question.reading');
    try {
      const state = getState();
      const gameObject = state.game?.game;
      if (!gameObject) return;

      const currentRoundState = gameObject.state?.roundState;
      if (currentRoundState !== GAMESTATE_ROUNDSTATE_FINALROUND_QUESTION_INTRO) {
        log.warn(`FinalQuestionIntro.onComplete skipping advance — state already past intro (${currentRoundState})`);
        return;
      }

      const players = Object.values(state.player?.entities || {});
      await advanceState(gameObject, players, dispatch);
    } catch (error) {
      log.error('Error advancing after final question intro:', error);
    }
  },
};

/**
 * Playback 2 — "[reads clue]. You have 30 seconds, good luck!"
 * Plays during `question.reading`.
 * onComplete: advance to question.answering (timer starts).
 */
export const FinalQuestionReadPlayback = {
  id: 'FINAL_QUESTION_READ_PLAYBACK',
  momentType: MOMENT_TYPES.FINAL_QUESTION_READ,

  shouldPlay: (gameState, hostState, narration, playedNarrations) => {
    if (!narration || narration.momentType !== MOMENT_TYPES.FINAL_QUESTION_READ) return false;
    if (playedNarrations.has(narration.key)) return false;

    const inReading =
      gameState?.currentState === GAMESTATE_ROUND &&
      gameState?.round === GAMESTATE_ROUND_FINAL &&
      gameState?.roundState === GAMESTATE_ROUNDSTATE_QUESTION_READING;
    if (!inReading) return false;

    const ready = narration.textStatus === 'READY' && (narration.audioStatus === 'READY' || narration.audioStatus === 'ERROR');
    if (!ready) return false;

    if (hostState?.status === HOST_STATUS.SPEAKING) return false;

    return true;
  },

  onComplete: async (gameId, dispatch, getState) => {
    log.info('Final question read complete, advancing to answering');
    try {
      const state = getState();
      const gameObject = state.game?.game;
      if (!gameObject) return;

      const currentRoundState = gameObject.state?.roundState;
      if (currentRoundState !== GAMESTATE_ROUNDSTATE_QUESTION_READING) {
        log.warn(`FinalQuestionRead.onComplete skipping advance — state already past reading (${currentRoundState})`);
        return;
      }

      const players = Object.values(state.player?.entities || {});
      await advanceState(gameObject, players, dispatch);
    } catch (error) {
      log.error('Error advancing after final question read:', error);
    }
  },
};

const api = {
  trigger: FinalQuestionReadTrigger,
  playback: [FinalQuestionIntroPlayback, FinalQuestionReadPlayback],
};

export default api;
